// The calculator's suggested amount, filled into a loan: what the loan would
// be with it (the API's quote, the one place the figures are worked out), and
// saving it. Shared by Loan Details (which fills it in and offers Update) and
// the edit form (which fills it in itself and saves with its own Submit).
import { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import { loanDeductionDay, pendingSuggestion } from './suggestion';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * What the loan would be with the suggested amount and term: the quote
 * (principal + interest, the expenses, the total, the monthly payment and
 * the deduction dates). enabled: only where the loan can still be changed.
 * expenseCodes: the expenses the loan carries (null until they are loaded).
 */
export const useSuggestedTerms = ({ loan, calculator, expenseCodes, enabled }) => {
  const suggestion = enabled ? pendingSuggestion(loan, calculator) : null;
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const codesKey = expenseCodes ? JSON.stringify(expenseCodes) : null;
  const deductionDay = loanDeductionDay(loan);

  useEffect(() => {
    setQuote(null);
    setError('');
    if (!suggestion || codesKey === null || !loan?.id) return undefined;
    let alive = true;
    const params = {
      principal_amount: suggestion.amount,
      duration_months: suggestion.months,
      interest_rate: loan.interest_rate,
      additional_expenses: codesKey,
    };
    if (loan.client_id) params.client_id = loan.client_id;
    if (deductionDay) params.deduction_day = deductionDay;
    if (loan.credit_assessment_id) params.credit_assessment_id = loan.credit_assessment_id;
    axios.get(apiUrl('/api/loans/quote'), { params, headers: headers() })
      .then((res) => { if (alive) setQuote(res.data || null); })
      .catch((e) => { if (alive) setError(e?.response?.data?.error || 'The suggested amount could not be worked out.'); });
    return () => { alive = false; };
  }, [loan?.id, loan?.interest_rate, loan?.client_id, loan?.credit_assessment_id, deductionDay, suggestion?.amount, suggestion?.months, codesKey]);

  return { suggestion, quote, error };
};

/**
 * Save the quoted terms on the loan (PUT /api/loans/:id). A loan already
 * signed goes back to the client to sign the new agreement (the API's
 * sendBackForSigning). Returns the updated loan.
 */
export const saveQuotedTerms = async (loan, quote) => {
  const res = await axios.put(apiUrl(`/api/loans/${loan.id}`), {
    principal_amount: quote.principal_amount,
    interest_rate: quote.interest_rate,
    duration_months: quote.duration_months,
    deduction_day: loanDeductionDay(loan),
    ...(quote.start_date ? { start_date: quote.start_date } : {}),
    ...(quote.due_date ? { due_date: quote.due_date } : {}),
  }, { headers: headers() });
  return res.data;
};

/**
 * Start the affordability calculation again on the loan (it tests the loan's
 * new instalment). Resolves to the assessment id, or null when it cannot run
 * (no calculation on the loan, or past the stage where it can).
 */
export const startRecalculation = async (loanId) => {
  try {
    const res = await axios.post(apiUrl(`/api/loans/${loanId}/recalculate`), {}, { headers: headers() });
    return res.data?.assessment_id ?? null;
  } catch {
    return null;
  }
};
